import React, { useState, useEffect } from 'react';
import LoadingIndicator from '../components/common/LoadingIndicator';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import '../styles/ServiceConfigPage.css';
import { useSession } from '../context/SessionContext';
import { fetchParameterNames, fetchServiceNames } from '../services/serviceConfigService';
import axios from '../services/axiosConfig';
 
const ParameterConfigPage = () => {
  const [parameters, setParameters] = useState([]);
  const [parameterForm, setParameterForm] = useState({
    name: '',
    source: '',
    handleNull: '1',
    valueSourcePath: '',
    serviceName: '',
    keySourcePath: '',
    regex: '',
    isActive: true,
    serviceID: '' // Added serviceID field
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paramError, setParamError] = useState(null);
  const [showIsActiveField, setShowIsActiveField] = useState(false);
  const [showRegexField, setShowRegexField] = useState(false);
  const [showServiceNameField, setShowServiceNameField] = useState(false);
  const [showKeySourcePathField, setShowKeySourcePathField] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]); // State to hold service options
  const [isNewParameter, setIsNewParameter] = useState(true); // State to track new parameter creation
  const { sessionData } = useSession();
  
  // Use environment variable for API URL (consistent with CustomReports pattern)
  const API_URL = process.env.REACT_APP_API_URL;
  
  useEffect(() => {
    loadParameterNames();
  
    loadServiceNames();
  }, []);
 
  // Ensure the `isActive` tab is always shown when the API is called, without checking the source.
  const loadParameterNames = async () => {
    try {
      const response = await axios.get(`${API_URL}/ServiceConfig/LoadParameterName`);
      const fetchedParameters = response.data?.serviceParameter;
 
      if (Array.isArray(fetchedParameters)) {
        setParameters(fetchedParameters);
        setShowIsActiveField(true); // Always show the isActive tab when the API is called
      } else {
        console.error("Unexpected data structure for parameters:", response.data);
      }
    } catch (err) {
      console.error("Error loading parameter names:", err);
    }
  };

   // Fetch service names from external API
   const loadServiceNames = async () => {
     try {
       setLoading(true);
       const services = await fetchServiceNames();

       // Transform data for react-select
       const options = services.map(service => ({
         value: service.id,
         label: service.name
       }));

       setServiceOptions(options);
     } catch (error) {
       console.error('Error loading service names:', error);
       setParamError('Failed to load service names');
     } finally {
       setLoading(false);
     }
   };
 
  const initialFormState = {
    name: '',
    source: '',
    handleNull: '1',
    valueSourcePath: '',
    serviceName: '',
    keySourcePath: '',
    regex: '',
    isActive: true,
    serviceID: '' // Added serviceID field to initial state
  };
 
  // Add debugging logs to identify why the source function is not working.
  const handleSourceChange = (e) => {
    const source = e.target.value;

    setParameterForm((prevForm) => {
      const updatedForm = {
        ...prevForm,
        source, // Update only the source field
      };

      // Show/hide fields based on source
      if (source === 'WebService') {
        setShowServiceNameField(true);
        setShowKeySourcePathField(true);
        setShowRegexField(false);
      } else if (source === 'QueryString' || source === 'ADFS') {
        setShowServiceNameField(false);
        setShowKeySourcePathField(false);
        setShowRegexField(true);
      } else {
        setShowServiceNameField(false);
        setShowKeySourcePathField(false);
        setShowRegexField(false);
      }

      return updatedForm; // Ensure the name field is preserved
    });
  };
 
  // Remove debounce logic and ensure the parameter name remains consistent.
  const handleParameterFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setParameterForm((prevForm) => ({
      ...prevForm, // Preserve existing fields
      [name]: type === 'checkbox' ? checked : value, // Update only the changed field
    }));
 
    if (name === 'name' && value) {
      fetchParameterDetails(value);
    }
  };
 
  const handleParameterChange = (selectedOption) => {
    setParameterForm((prevForm) => ({
      ...prevForm,
      name: selectedOption.value,
    }));
    fetchParameterDetails(selectedOption.value);
  };
 
  const fetchServiceNameById = async (serviceID) => {
    try {
      const fetchedServices = await fetchServiceNames();
 
      // Find the service with the matching serviceID
      const matchedService = fetchedServices.find((service) => service.id === serviceID);
 
      if (matchedService) {
        setParameterForm((prevForm) => ({
          ...prevForm,
          serviceName: matchedService.name, // Set the service name in the form
        }));
      } else {
        console.warn(`No service found with ID: ${serviceID}`);
      }
    } catch (err) {
      console.error('Error fetching service names:', err);
    }
  };
 
  // Update the `fetchParameterDetails` function to dynamically show tabs based on the service name logic.
  const fetchParameterDetails = async (parameterName) => {
    try {
      const response = await axios.get(`${API_URL}/ServiceConfig/GetParameterDetails?parameterName=${parameterName}`);
 
      const parameterDetails = response.data[0];
 
      if (parameterDetails) {
        setParameterForm((prevForm) => ({
          ...prevForm,
          id: parameterDetails.id || '', // Ensure id is set
          name: parameterName, // Ensure the parameter name remains unchanged
          source: parameterDetails.source || '',
          valueSourcePath: parameterDetails.valueSourcePath || '',
          regex: parameterDetails.regex || '',
          serviceName: '', // Temporarily set to empty, will be updated after fetching service name
          keySourcePath: parameterDetails.keySourcePath || '',
          isActive: parameterDetails.isActive !== undefined ? parameterDetails.isActive : true,
          handleNull: parameterDetails.handleNull || 'ASEmpty',
          serviceID: parameterDetails.serviceID || '' // Set serviceID from parameter details
        }));
 
        // Fetch and set the service name if serviceID is present
        if (parameterDetails.serviceID) {
          const matchedService = serviceOptions.find(service => service.value === parameterDetails.serviceID);
          if (matchedService) {
            setParameterForm(prevForm => ({
              ...prevForm,
              serviceName: matchedService.label // Set the service name based on the matched service ID
            }));
          }
        }
 
        // Dynamically update the UI based on the service name logic
        if (parameterDetails.source === 'WebService') {
          setShowServiceNameField(true);
          setShowKeySourcePathField(true);
          setShowRegexField(false);
        } else if (parameterDetails.source === 'QueryString' || parameterDetails.source === 'ADFS') {
          setShowServiceNameField(false);
          setShowKeySourcePathField(false);
          setShowRegexField(true);
        } else {
          setShowServiceNameField(false);
          setShowKeySourcePathField(false);
          setShowRegexField(false);
        }
      } else {
        console.error("No parameter details found for the selected parameter name.");
      }
    } catch (err) {
      console.error("Error fetching parameter details:", err);
    }
  };
 
  // Update the saveParameter function to send a POST request to the SaveParameter API
  const saveParameter = async () => {
    try {
      const getUserId = () => {
        try {
          // Try to get userId from sessionData first
          if (sessionData && sessionData.userId) {
            return sessionData.userId;
          }
          // Fallback to session storage
          const userStr = sessionStorage.getItem('user');
          if (userStr) {
            const userData = JSON.parse(userStr);
            return userData.userId || userData.UserID || userData.userID || userData.UserId || 1;
          }
        } catch (err) {
          console.error('Error getting user ID from session', err);
        }
        return 1; // Default user ID if not found
      };

      const payload = {
        parameterName: parameterForm.name.trim() || '',
        keySource: parameterForm.keySourcePath.trim() || '',
        valueSource: parameterForm.valueSourcePath.trim() || '',
        source: parameterForm.source.trim() || '',
        serviceId: parameterForm.serviceID || '', // Use serviceID for the selected service
        regEx: parameterForm.regex.trim() || '',
        showEmptyAsNull: 'ASEmpty', // Always set to ASEmpty
        userID: getUserId(),
      };

      const response = await axios.post(`${API_URL}/ServiceConfig/SaveParameter`, payload);
      alert('Parameter saved successfully!');

      // Reset form and reload data
      setParameterForm(initialFormState);
      loadParameterNames();
    } catch (error) {
      console.error('Error saving parameter:', error);
      alert('Failed to save parameter. Please try again.');
    }
  };
 
  // Update the HTTP method to PUT for the Edit button functionality.
  // Add additional logging to verify the `paramId` and other fields in the payload.
  // Ensure `keySourcePath` is sent as an empty string if it has no value.
  // Add a log to inspect the payload before hitting the backend.
  const editParameter = async () => {
    try {
      const getUserId = () => {
        try {
          // Try to get userId from sessionData first
          if (sessionData && sessionData.userId) {
            return sessionData.userId;
          }
          // Fallback to session storage
          const userStr = sessionStorage.getItem('user');
          if (userStr) {
            const userData = JSON.parse(userStr);
            return userData.userId || userData.UserID || userData.userID || userData.UserId || 1;
          }
        } catch (err) {
          console.error('Error getting user ID from session', err);
        }
        return 1; // Default user ID if not found
      };

      const payload = {
        paramId: parameterForm.id ? parameterForm.id.toString() : '', // Add fallback for id
        keySource: parameterForm.keySourcePath.trim() || '',
        valueSource: parameterForm.valueSourcePath.trim() || '',
        source: parameterForm.source.trim() || '',
        serviceId: parameterForm.serviceID ? parameterForm.serviceID.toString() : '', // Use serviceID instead of serviceName
        regEx: parameterForm.regex.trim() || '',
        isActive: parameterForm.isActive ? '1' : '0', // Convert boolean to string
        showEmptyAsNull: parameterForm.handleNull || 'ASEmpty',
        userID: getUserId(), // Get userId from session
      };
 
      console.log('Editing parameter with payload:', payload);
      const response = await axios.put(`${API_URL}/ServiceConfig/EditParameter`, payload);
      alert('Parameter updated successfully!');
      // Reset form and reload data
      setParameterForm(initialFormState);
      loadParameterNames();
    } catch (error) {
      console.error('Error editing parameter:', error);
      alert('Failed to edit parameter. Please try again.');
    }
  };
 
  // Update the form submission handler to call the saveParameter function.
  const handleFormSubmit = (e) => {
    e.preventDefault();
    saveParameter();
  };
 
  const customStyles = {
    control: (provided) => ({
      ...provided,
      width: '300px', // Set the width of the dropdown
    }),
  };
 
 // ...existing code...
  return (
    <div className="elegant-aero">
      <span className="span-title">Service Config</span>
    
      <div className="panel-content parameter-panel-content">
        <form onSubmit={handleFormSubmit}>
          {/* Parameter Name */}
          <div className="form-group">
            <label htmlFor="parameterName">Parameter Name <span className="required-indicator">*</span></label>
            <div className="parameter-name-container">
              <CreatableSelect
                id="parameterName"
                name="name"
                options={parameters.map((param) => ({ value: param.name, label: param.name }))}
                value={parameterForm.name ? { value: parameterForm.name, label: parameterForm.name } : null}
                onChange={(selectedOption) => {
                  const value = selectedOption ? selectedOption.value : '';
                  setParameterForm((prevForm) => ({ ...prevForm, name: value }));

                  // Check if the selected option exists in the parameters list
                  const isExisting = parameters.some((param) => param.name === value);
                  setShowIsActiveField(isExisting); // Show "Edit" button if existing, otherwise "Save"
                  setIsNewParameter(!isExisting); // Update isNewParameter state

                  if (isExisting) {
                    fetchParameterDetails(value); // Fetch and display parameter details
                  }
                }}
                onCreateOption={(inputValue) => {
                  const newOption = { value: inputValue, label: inputValue };
                  setParameters((prev) => {
                    const updatedParameters = [...prev, { name: inputValue }];
                    return updatedParameters;
                  });
                  setParameterForm((prevForm) => ({ ...prevForm, name: inputValue }));

                  // Ensure "Save" button is displayed for new options
                  setShowIsActiveField(false);
                  setIsNewParameter(true); // Mark as a new parameter
                }}
                isSearchable 
              />
            </div>
          </div>

          {/* Handle when source returns NULL */}
          <div className="handle-null-container">
            <label className="handle-null-label">Handle when source returns NULL</label>
            <div className="handle-null-options">
              <div className="handle-null-option">
                <input
                  type="radio"
                  name="handleNull"
                  value="ASEmpty"
                  checked={parameterForm.handleNull === 'ASEmpty'}
                  onChange={handleParameterFormChange}
                />
                <label className="handle-null-option-label">As Empty (SSRS parameter will be empty)</label>
              </div>
              <div className="handle-null-option">
                <input
                  type="radio"
                  name="handleNull"
                  value="IgnoreNull"
                  checked={parameterForm.handleNull === 'IgnoreNull'}
                  onChange={handleParameterFormChange}
                />
                <label className="handle-null-option-label">Ignore Null (SSRS parameter will show default SSRS parameter values)</label>
              </div>
            </div>
          </div>

          {/* Source */}
          <div className="form-group">
            <label htmlFor="source">Source</label>
            <select
              id="source"
              name="source"
              className="form-control "
              value={parameterForm.source} // Bind to parameterForm.source
              onChange={handleSourceChange}
              required
              style ={{ width: '150px' }} // Set width for the select dropdown
            >
              <option value="">Select</option>
              <option value="QueryString">QueryString</option>
              <option value="WebService">WebService</option>
              <option value="ServiceParam">ServiceParam</option>
              <option value="Session">Session</option>
              <option value="ADFS">ADFS</option>
            </select>
          </div>

          {/* Service Name Field */}
          {showServiceNameField && (
            <div className="form-group">
              <label htmlFor="serviceName">Service Name</label>
              <Select
                id="serviceName"
                name="serviceName"
                value={serviceOptions.find(option => option.value === parameterForm.serviceID)}
                onChange={(option) => setParameterForm(prev => ({ ...prev, serviceID: option.value }))}
                options={serviceOptions}
                isDisabled={loading}
                placeholder="Select Service"
                className="basic-select"
                classNamePrefix="select"
              />
            </div>
          )}

          {/* Key Source Path */}
          {showKeySourcePathField && (
            <div className="form-group">
              <label htmlFor="keySourcePath">Key Source Path</label>
              <input
                type="text"
                id="keySourcePath"
                name="keySourcePath"
                className="form-control "
                value={parameterForm.keySourcePath} // Bind to parameterForm.keySourcePath
                onChange={handleParameterFormChange}
                style={{ width: '300px' }} // Set width for the input field
              />
            </div>
          )}

          {/* Value Source Path */}
          <div className="form-group">
            <label htmlFor="valueSourcePath">Value Source Path</label>
            <input
              type="text"
              id="valueSourcePath"
              name="valueSourcePath"
              className="form-control "
              value={parameterForm.valueSourcePath} // Bind to parameterForm.valueSourcePath
              onChange={handleParameterFormChange}
              required
               style={{ width: '300px' }} // Set width for the input field
            />
          </div>

          {/* Regex Field */}
          {showRegexField && (
            <div className="form-group">
              <label htmlFor="regex">Regex</label>
              <input
                type="text"
                id="regex"
                name="regex"
                className="form-control "
                value={parameterForm.regex} // Bind to parameterForm.regex
                onChange={handleParameterFormChange}
                style={{ width: '300px' }} // Set width for the input field
              />
            </div>
          )}

          {/* IsActive Field */}
          {parameters.some((param) => param.name === parameterForm.name) && (
            <div className="form-group">
              <label htmlFor="isActive">Is Active</label>
              <select
                id="isActive"
                name="isActive"
                className="form-control "
                value={parameterForm.isActive ? 'True' : 'False'} // Bind to parameterForm.isActive
                onChange={(e) =>
                  setParameterForm((prevForm) => ({
                    ...prevForm,
                    isActive: e.target.value === 'True',
                  }))
                }
                style={{ width: '150px' }} // Set width for the select dropdown
              >
                <option value="True">True</option>
                <option value="False">False</option>
              </select>
            </div>
          )}

          {/* Save or Edit Button */}
          <div className="button-container panel-content">
            {isNewParameter ? (
              <button
                type="submit"
                className="save-button"
              >
                Save
              </button>
            ) : (
              <button
                type="button"
                className="save-button"
                onClick={editParameter} // Call the editParameter function on click
              >
                Edit
              </button>
            )}
          </div>

          {paramError && <div className="error-message">{paramError}</div>}
        </form>
      </div>
    </div>
  );

};
 
export default ParameterConfigPage;